import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, FileSpreadsheet, Building2, CreditCard, Leaf, Sparkles, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Data nodes around the brain hub. Positions are in the SVG viewBox (420 x 320).
const HUB = { x: 210, y: 160 };
const NODES = [
  { key: 'bills', label: 'Bills', icon: FileSpreadsheet, x: 64, y: 104, color: '#0ea5e9' },
  { key: 'vendors', label: 'Vendors', icon: Building2, x: 356, y: 104, color: '#6366f1' },
  { key: 'payments', label: 'Payments', icon: CreditCard, x: 64, y: 216, color: '#f59e0b' },
  { key: 'sustainability', label: 'Sustainability', icon: Leaf, x: 356, y: 216, color: '#10b981' },
];

const SCENARIOS = [
  { q: 'Which vendors need my attention this week?', fire: ['bills', 'payments', 'vendors'],
    a: '6 vendors need attention — 2 are 30+ days overdue, 3 have missing bills, 1 has a payment mismatch.', chips: ['Bills', 'Payments', 'Vendors'] },
  { q: 'Build a portfolio sustainability report.', fire: ['sustainability', 'bills'],
    a: '128 properties · 12,450 tCO₂e for the trailing year — your branded PDF is ready.', chips: ['Sustainability', 'PDF'] },
  { q: 'Draft follow-ups for every overdue vendor.', fire: ['bills', 'vendors'],
    a: '8 vendor emails drafted and queued for your approval — nothing is sent without you.', chips: ['Vendors', 'Drafts'] },
];

const pct = (v, max) => `${(v / max) * 100}%`;

function usePrefersReducedMotion() {
  const [r, setR] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) setR(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);
  return r;
}

export default function AstridBrainDemo() {
  const reduced = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('query'); // query -> thinking -> answer -> hold
  const alive = useRef(true);
  const s = SCENARIOS[idx];

  useEffect(() => {
    alive.current = true;
    if (reduced) { setPhase('answer'); return () => { alive.current = false; }; }
    let t;
    const seq = [
      ['query', 1400],
      ['thinking', 2200],
      ['answer', 2600],
      ['hold', 700],
    ];
    let i = 0;
    const step = () => {
      if (!alive.current) return;
      const [p, d] = seq[i];
      setPhase(p);
      t = setTimeout(() => {
        i += 1;
        if (i >= seq.length) { i = 0; setIdx((n) => (n + 1) % SCENARIOS.length); }
        step();
      }, d);
    };
    step();
    return () => { alive.current = false; clearTimeout(t); };
  }, [reduced, idx]);

  const firing = phase === 'thinking' || phase === 'answer';

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-2xl shadow-slate-900/[0.10] dark:border-white/10 dark:from-[#0b0b12] dark:to-[#0d0d1a]"
      data-testid="astrid-brain-demo"
    >
      {/* chrome */}
      <div className="flex items-center gap-2 border-b border-slate-200/70 px-4 py-2.5 dark:border-white/10">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand text-white"><Brain className="h-3.5 w-3.5" /></span>
        <span className="text-xs font-bold text-slate-800 dark:text-white">Astrid</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-semibold text-brand"><Sparkles className="h-3 w-3" /> Neural engine</span>
        <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-slate-400">
          {SCENARIOS.map((_, i) => (<span key={i} className={cn('h-1.5 rounded-full transition-all', i === idx ? 'w-5 bg-brand' : 'w-1.5 bg-slate-300 dark:bg-white/20')} />))}
        </span>
      </div>

      {/* Query pill */}
      <div className="px-4 pt-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={reduced ? false : { opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            className="mx-auto flex w-fit max-w-full items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand" /> {s.q}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Neural stage */}
      <div className="relative mx-auto w-full" style={{ aspectRatio: '420 / 320' }}>
        {/* connectors + signals */}
        <svg viewBox="0 0 420 320" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          <defs>
            <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--brand, #ff5757)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--brand, #ff5757)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx={HUB.x} cy={HUB.y} r="120" fill="url(#hubGlow)" />
          {NODES.map((n) => {
            const active = firing && s.fire.includes(n.key);
            return (
              <g key={n.key}>
                <line x1={HUB.x} y1={HUB.y} x2={n.x} y2={n.y} stroke={n.color}
                  strokeOpacity={active ? 0.55 : 0.18} strokeWidth={active ? 2 : 1.25}
                  strokeDasharray="4 6" />
                {!reduced && (
                  <motion.circle
                    r={active ? 3.6 : 2.4} fill={n.color}
                    initial={false}
                    animate={{ cx: [HUB.x, n.x, HUB.x], cy: [HUB.y, n.y, HUB.y], opacity: active ? [0.2, 1, 0.2] : [0.1, 0.5, 0.1] }}
                    transition={{ duration: active ? 1.1 : 2.2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* Brain hub */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {!reduced && [0, 1, 2].map((i) => (
            <motion.span key={i}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand/30"
              style={{ width: 56, height: 56 }}
              animate={{ scale: [1, 2.6], opacity: [0.5, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.8, ease: 'easeOut' }}
            />
          ))}
          <motion.div
            className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white shadow-lg shadow-brand/40"
            animate={reduced ? {} : { scale: firing ? [1, 1.08, 1] : 1 }}
            transition={{ duration: 0.9, repeat: firing ? Infinity : 0, ease: 'easeInOut' }}
          >
            <Brain className="h-7 w-7" />
          </motion.div>
        </div>

        {/* Data nodes */}
        {NODES.map((n) => {
          const active = firing && s.fire.includes(n.key);
          return (
            <div key={n.key} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: pct(n.x, 420), top: pct(n.y, 320) }}>
              <motion.div
                animate={reduced ? {} : { scale: active ? [1, 1.12, 1] : 1 }}
                transition={{ duration: 0.7, repeat: active ? Infinity : 0 }}
                className={cn(
                  'flex items-center gap-1.5 rounded-xl border bg-white px-2.5 py-1.5 shadow-sm transition-colors dark:bg-[#12121c]',
                  active ? 'border-brand/60 shadow-md' : 'border-slate-200 dark:border-white/10'
                )}
              >
                <n.icon className="h-3.5 w-3.5" style={{ color: n.color }} />
                <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-200">{n.label}</span>
              </motion.div>
            </div>
          );
        })}

        {/* Thinking label */}
        <AnimatePresence>
          {phase === 'thinking' && !reduced && (
            <motion.div
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/85 px-3 py-1 text-[10px] font-medium text-white backdrop-blur dark:bg-white/10"
            >
              Firing neurons · querying data · building context…
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Answer */}
      <div className="min-h-[112px] px-4 pb-4">
        <AnimatePresence mode="wait">
          {(phase === 'answer' || phase === 'hold' || reduced) && (
            <motion.div
              key={idx + '-a'}
              initial={reduced ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-start gap-2.5"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"><Brain className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-700 shadow-sm dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200">
                  {s.a}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Grounded</span>
                  {s.chips.map((c) => (
                    <span key={c} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-white/10 dark:text-slate-300">{c}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="border-t border-slate-200/70 px-4 py-2 text-center text-[10px] text-slate-400 dark:border-white/10">
        Illustrative — Astrid queries only the data you're permitted to see.
      </div>
    </div>
  );
}
